import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { hoursLabel } from "./scheduleFormatters";
import TimeCardDrawer from "./TimeCardDrawer";
import { FLAG_BADGES, STATUS_BADGES, cardDateLabel, rangeLabel } from "./timesheetFormatters";

const textStyle = (size, color, weight = 400) => ({ fontSize: size, lineHeight: `${size + 6}px`, color: `var(--sl-${color})`, fontWeight: weight });

const RecentTimeCards = ({ staffMemberId, paths, currentPayPeriod = false }) => {
  const [cards, setCards] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(
    (isCurrent = () => true) =>
      client
        .get(paths.timeCards, { params: { staff_member_id: staffMemberId, current_pay_period: currentPayPeriod || undefined } })
        .then(({ data }) => {
          if (!isCurrent()) return;
          setFailed(false);
          setCards(data.timeCards);
        })
        .catch(() => {
          if (!isCurrent()) return;
          setFailed(true);
          setCards([]);
        }),
    [paths.timeCards, staffMemberId, currentPayPeriod]
  );

  useEffect(() => {
    let current = true;
    load(() => current);
    return () => {
      current = false;
    };
  }, [load]);

  if (cards === null) return null;

  return (
    <div>
      <div className="mb-2" style={textStyle(16, "text-primary", 600)}>
        Recent time cards
      </div>
      {failed && <div style={textStyle(14, "text-error")}>Couldn't load time cards.</div>}
      {!failed && cards.length === 0 && <div style={textStyle(14, "text-tertiary")}>{currentPayPeriod ? "No punches yet this pay period." : "No time cards yet."}</div>}
      {cards.map((card) => (
        <div key={card.id} className="d-flex justify-content-between" style={{ gap: 12, padding: "10px 0", borderTop: "1px solid var(--sl-border-secondary)" }}>
          <div style={{ minWidth: 0 }}>
            <div style={textStyle(14, "text-primary", 600)}>{cardDateLabel(card.clockInAt)}</div>
            <div style={textStyle(12, "text-quaternary")}>
              {card.eventTitle || card.venueName} · {rangeLabel(card.clockInAt, card.clockOutAt)}
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 4, marginTop: 2 }}>
              {card.flags.map((flag) => (
                <Badge key={flag} size="sm" color={FLAG_BADGES[flag]?.color || "gray"}>
                  {FLAG_BADGES[flag]?.label || flag}
                </Badge>
              ))}
            </div>
          </div>
          <div className="d-flex flex-column align-items-end" style={{ gap: 4, flexShrink: 0 }}>
            <span style={textStyle(14, "text-secondary", 600)}>{hoursLabel(card.workedMinutes)} h</span>
            <Badge size="sm" color={STATUS_BADGES[card.status].color}>
              {STATUS_BADGES[card.status].label}
            </Badge>
            {card.canEdit && (
              <Button size="xs" variant="link-color" onClick={() => setEditing(card)}>
                Edit
              </Button>
            )}
          </div>
        </div>
      ))}
      {editing && (
        <TimeCardDrawer
          card={editing}
          paths={paths}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
};

RecentTimeCards.propTypes = {
  staffMemberId: PropTypes.number.isRequired,
  paths: PropTypes.shape({ timeCards: PropTypes.string.isRequired, timeCard: PropTypes.string.isRequired }).isRequired,
  currentPayPeriod: PropTypes.bool,
};

export default RecentTimeCards;
